import * as CryptoJS from "crypto-js";

interface StoredPassword {
    value: string;
    salt: string;
}

interface AccountInfo {
    account: string;
    password: string;
    expires?: number;
}

export class RememberPassword {
    private static readonly STORAGE_KEY = Symbol("REMO_ACCOUNT").toString();

    /**
     * 生成唯一的加密密钥
     * @param salt 盐值
     * @returns 加密密钥
     */
    private static generateKey(salt: string, info: string): string {
        // 使用设备信息和运行时信息来生成密钥
        const browserInfo = [info, new Date().getTimezoneOffset()].join("|");

        return CryptoJS.PBKDF2(browserInfo, salt, {
            keySize: 256 / 32,
            iterations: 1000,
        }).toString();
    }

    /**
     * 保存密码
     * @param password 要保存的密码
     * @param expiresInDays 过期天数，undefined表示永不过期
     */
    public static savePassword(
        account: AccountInfo,
        cryptoKey: string,
        expiresInDays?: number,
    ): void {
        try {
            // 为每个密码生成唯一的盐值
            const salt = CryptoJS.lib.WordArray.random(128 / 8).toString();
            const key = this.generateKey(salt, cryptoKey);

            const allInfo: AccountInfo = {
                ...account,
                expires: expiresInDays ? Date.now() + expiresInDays * 86400000 : undefined,
            };

            const encryptedPass = CryptoJS.AES.encrypt(JSON.stringify(allInfo), key).toString();

            const storedData: StoredPassword = {
                value: encryptedPass,
                salt,
            };

            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(storedData));
        } catch (error) {
            console.error("保存密码失败");
            throw new Error("保存密码失败");
        }
    }

    /**
     * 获取保存的密码
     * @returns 解密后的密码，如果密码已过期或不存在则返回null
     */
    public static getPassword(cryptoKey: string): string | null {
        try {
            const storedData = localStorage.getItem(this.STORAGE_KEY);
            if (!storedData) {
                return null;
            }

            const { value, salt } = JSON.parse(storedData) as StoredPassword;

            const key = this.generateKey(salt, cryptoKey);

            const bytes = CryptoJS.AES.decrypt(value, key);
            const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8)) as AccountInfo;

            if (decryptedData.expires && Date.now() > decryptedData.expires) {
                this.clearPassword();
                return null;
            }

            return bytes.toString(CryptoJS.enc.Utf8);
        } catch (error) {
            console.error("获取密码失败");
            return null;
        }
    }

    /**
     * 清除保存的密码
     */
    public static clearPassword(): void {
        try {
            localStorage.removeItem(this.STORAGE_KEY);
        } catch (error) {
            console.error("清除密码失败");
            throw new Error("清除密码失败");
        }
    }

    /**
     * 检查是否有保存的密码
     */
    public static hasStoredPassword(): boolean {
        const cryptoKey = (window as any).isElectron
            ? (window as any).node.os.hostname()
            : navigator.userAgent;
        const info = this.getPassword(cryptoKey);
        return Boolean(info ? Boolean(JSON.parse(info).password) : false);
    }

    /**
     * 更新密码过期时间
     * @param expiresInDays 新的过期天数
     */
    public static updateExpiration(cryptoKey: string, expiresInDays?: number): void {
        const accountInfo = this.getPassword(cryptoKey);
        if (accountInfo) {
            this.savePassword(JSON.parse(accountInfo), cryptoKey, expiresInDays);
        }
    }
}
